import { create } from "zustand";
import type { AgentThreadDetail, AgentThreadRef, ResumeTourRef } from "@/lib/agent-sessions";

// UI + transient state for the Assistant (the in-app AI chat that streams
// from the agent-service via the BFF /ai/stream passthrough). Distinct from
// the person-to-person chat-store: the Assistant has no GraphQL conversation
// entity — messages are an in-memory thread, lost on reload (acceptable for
// v1). `agentThreadId` is the agent-service's conversation-memory id (its
// wire field is `sessionId`); resent across turns so the Assistant remembers
// context. It is NOT the BFF login Session id.

export interface AgentMessage {
  // `info` is a user-triggered, non-LLM person profile load (ADR-0014): rendered as a
  // distinct info block, not a chat bubble. Streamed into like an assistant message.
  id: string;
  role: "user" | "assistant" | "info";
  content: string;
  error?: string;
  // Explicit border color (live tour bubbles: the theme-frozen pillar color). When absent, the
  // renderer derives the border from `section` instead.
  color?: string;
  // Tour hierarchy indent (0 = section intro, 1 = page summary, 2 = page stop) — tour bubbles
  // render indented so the outline structure is visible. Persisted with tours.
  indent?: number;
  // Which section the message belongs to (a turn's subsection like "growth"/"save/profile", or a
  // tour step's pillar like "grow"); the renderer maps it to the border color. Persisted.
  section?: string;
  // Tour-step revisit target: clicking the bubble navigates to `route` (if set) and pulses the
  // `[data-assistant-anchor=anchor]` element. Persisted with tours; also set on live tour bubbles.
  anchor?: string;
  route?: string;
}

// One "what the user currently sees" fact (AG-UI Context shape). Registered by the mounted page
// via useAssistantContext and merged into every turn's uiContext (agent-service ADR 0020, motive 1).
export interface AssistantContextItem {
  description: string;
  value: string;
}

// One stop of a page's guided tour (/tour): highlight the [data-assistant-anchor=anchor] element
// and post title+description in the chat. Registered by the mounted page via useAssistantTour with
// live data baked into the strings.
export interface TourStop {
  anchor: string;
  title: string;
  description: string;
  /** Hand-tuned display time for THIS stop, verbatim (skips the driver's base + reading-time). */
  dwellMs?: number;
}

export interface PageTour {
  route: string;
  // Short localized page name incl. its pillar ("Save Documents") — prefixes every tour bubble
  // so a snippet names the (sub)section it belongs to.
  label: string;
  summary: string;
  stops: TourStop[];
}

interface AgentState {
  open: boolean;
  // Which screen the window shows: the Chats list (history) or the active chat.
  // Defaults to the list ("land on history"); persists across minimize/reopen
  // (the panel stays mounted), resets to list only on reload.
  view: "list" | "chat";
  agentThreadId: string | null;
  // True while a turn is in flight — drives the typing dots and disables the
  // composer.
  pending: boolean;
  messages: AgentMessage[];

  // Chat history (past agent threads) shown in the list view.
  threads: AgentThreadRef[];
  threadsState: "idle" | "loading" | "error";

  // Agent-generated follow-up suggestions for the latest reply (the clickable
  // chips). Ephemeral: set on the `suggestions` SSE event, cleared when a new
  // turn starts / on newChat / openThread.
  suggestions: string[];

  // A prompt queued by `ask()` (e.g. the power-detail "ask the assistant" button) for the always-mounted
  // AssistantDrawer to send once it isn't mid-turn. null when nothing is queued.
  pendingSend: string | null;

  // Anchor name of an element the agent asked to highlight (HighlightElement tool, ADR 0020).
  // Consumed by the AssistantDrawer effect: scrollIntoView + pulse class, cleared after ~4s.
  highlightAnchor: string | null;
  // Snippet text shown as a floating card at the highlighted element while it pulses — set by
  // snippet clicks alongside highlightAnchor; null for plain agent highlights.
  highlightHint: { text: string } | null;

  // Live page context (motive 1, ADR 0020), keyed by the registering component's id. The mounted
  // page(s) write via useAssistantContext; the AssistantDrawer reads the flattened values at send
  // time (imperatively — NOT a reactive selector — so page-data churn never re-renders the drawer).
  uiContext: Record<string, AssistantContextItem[]>;

  // Guided-tour registrations (/tour), keyed by the registering component's id — same lifecycle as
  // uiContext (mounted page registers, replaces on data change, clears on unmount). The tour driver
  // reads these imperatively.
  pageTours: Record<string, PageTour>;
  // True while a /tour run is in flight; swaps the chips strip to the tour controls and reroutes Esc.
  tourActive: boolean;
  // True while the running tour is paused (Pause/Continue button label + driver gate).
  tourPaused: boolean;
  // True once the tour has finished and sits in review mode (controls stay, snippets clickable;
  // Stop/Esc exits). Distinct from an abort, which tears everything down immediately.
  tourFinished: boolean;
  // Message ids of the running tour's shown steps — those bubbles are clickable (revisit).
  // Render-safe mirror of the driver's step history (refs must not be read during render).
  tourStepIds: string[];
  // The step currently on screen (live position or a revisit target) — its bubble gets the
  // extra "you are here" highlight.
  tourCurrentStepId: string | null;
  // Dwell (ms) the current LIVE step is being shown for — the countdown bar fills over it so the
  // user sees the next step approaching. 0 = no countdown (revisit / review / teardown → bar hidden).
  tourStepDwellMs: number;
  // Mirror of the LOADED session's unfinished-tour state — no other source. Set from the
  // session detail on openThread (or locally right after a live interruption, which is the
  // same state the detail would report); cleared whenever no session with an interrupted tour
  // is loaded. The continue controls render only while this is set.
  resumeTour: ResumeTourRef | null;

  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  setAgentThreadId: (id: string) => void;
  setHighlightAnchor: (anchor: string | null) => void;
  setHighlightHint: (hint: { text: string } | null) => void;
  // Register/replace the calling component's live context items; clear removes them (on unmount).
  setUiContext: (id: string, items: AssistantContextItem[]) => void;
  clearUiContext: (id: string) => void;
  setPageTour: (id: string, tour: PageTour) => void;
  clearPageTour: (id: string) => void;
  setTourActive: (active: boolean) => void;
  setTourPaused: (paused: boolean) => void;
  setTourFinished: (finished: boolean) => void;
  addTourStepId: (id: string) => void;
  clearTourStepIds: () => void;
  setTourCurrentStepId: (id: string | null, dwellMs?: number) => void;
  setResumeTour: (resume: ResumeTourRef | null) => void;
  // Push one COMPLETE message (no pending/typing-dots) — the deterministic tour narration channel.
  // Returns the new message id (the tour driver links bubbles to revisitable steps by it).
  appendMessage: (
    role: AgentMessage["role"],
    content: string,
    opts?: Pick<AgentMessage, "color" | "indent" | "section" | "anchor" | "route">,
  ) => string;
  showList: () => void;
  setThreads: (threads: AgentThreadRef[]) => void;
  setThreadsState: (state: "idle" | "loading" | "error") => void;
  setSuggestions: (suggestions: string[]) => void;
  // Drop a deleted thread from the list; if it's the one currently loaded,
  // reset the active chat so a later send doesn't resend a dead thread id.
  removeThread: (id: string) => void;
  // Start a fresh chat: empty thread, no id, switch to the chat view.
  newChat: () => void;
  // Open the drawer and queue `text` to be sent (drained by AssistantDrawer). Appends to the open chat
  // if one is open (chat view + messages); otherwise starts a new chat first.
  ask: (text: string) => void;
  clearPendingSend: () => void;
  // Load a past thread's transcript into the chat view and continue it.
  openThread: (detail: AgentThreadDetail) => void;
  // Pushes the user message + an empty assistant message; returns the
  // assistant message id so the caller can stream deltas (or set the answer)
  // into it.
  startTurn: (userText: string, section?: string) => string;
  // Pushes a single empty `info` message (a profile load — no user bubble) and
  // returns its id so the caller can stream the profile into it. Sets `pending`
  // so the composer is disabled while it loads; leaves `suggestions` untouched.
  startInfoStream: () => string;
  appendDelta: (assistantId: string, delta: string) => void;
  endTurn: () => void;
  // Keeps the user message and any partial assistant text; records the error
  // on the assistant message and re-enables input.
  failTurn: (assistantId: string, error: string) => void;
}

function newId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

export const useAgentStore = create<AgentState>((set, get) => ({
  open: false,
  view: "list",
  agentThreadId: null,
  pending: false,
  messages: [],
  threads: [],
  threadsState: "idle",
  suggestions: [],
  pendingSend: null,
  highlightAnchor: null,
  highlightHint: null,
  uiContext: {},
  pageTours: {},
  tourActive: false,
  tourPaused: false,
  tourFinished: false,
  tourStepIds: [],
  tourCurrentStepId: null,
  tourStepDwellMs: 0,
  resumeTour: null,

  setOpen: (open) => set({ open }),
  toggleOpen: () => set((s) => ({ open: !s.open })),
  setAgentThreadId: (agentThreadId) => set({ agentThreadId }),
  setHighlightAnchor: (highlightAnchor) => set({ highlightAnchor }),
  setHighlightHint: (highlightHint) => set({ highlightHint }),
  setUiContext: (id, items) => set((s) => ({ uiContext: { ...s.uiContext, [id]: items } })),
  clearUiContext: (id) =>
    set((s) => {
      if (!(id in s.uiContext)) return s;
      const next = { ...s.uiContext };
      delete next[id];
      return { uiContext: next };
    }),
  setPageTour: (id, tour) => set((s) => ({ pageTours: { ...s.pageTours, [id]: tour } })),
  clearPageTour: (id) =>
    set((s) => {
      if (!(id in s.pageTours)) return s;
      const next = { ...s.pageTours };
      delete next[id];
      return { pageTours: next };
    }),
  setTourActive: (tourActive) => set({ tourActive }),
  setTourPaused: (tourPaused) => set({ tourPaused }),
  setTourFinished: (tourFinished) => set({ tourFinished }),
  addTourStepId: (id) => set((s) => ({ tourStepIds: [...s.tourStepIds, id] })),
  clearTourStepIds: () => set({ tourStepIds: [] }),
  setTourCurrentStepId: (tourCurrentStepId, dwellMs = 0) =>
    set({ tourCurrentStepId, tourStepDwellMs: dwellMs }),
  setResumeTour: (resumeTour) => set({ resumeTour }),
  appendMessage: (role, content, opts) => {
    const id = newId();
    set((s) => ({ messages: [...s.messages, { id, role, content, ...opts }] }));
    return id;
  },

  showList: () => set({ view: "list" }),
  setThreads: (threads) => set({ threads }),
  setThreadsState: (threadsState) => set({ threadsState }),
  setSuggestions: (suggestions) => set({ suggestions }),

  removeThread: (id) =>
    set((s) => ({
      threads: s.threads.filter((t) => t.id !== id),
      ...(s.agentThreadId === id ? { agentThreadId: null, messages: [], resumeTour: null } : {}),
    })),

  // Does not touch `pending`: any background turn keeps running and clears
  // `pending` itself when it ends.
  newChat: () =>
    set({ view: "chat", agentThreadId: null, messages: [], suggestions: [], resumeTour: null }),

  // Open + queue a prompt. A chat is "open" only when the drawer is open, on the chat view, with
  // messages — then we append; otherwise (closed drawer, list view, or empty) we start a new chat.
  ask: (text) => {
    const { open, view, messages } = get();
    const chatOpen = open && view === "chat" && messages.length > 0;
    set({
      open: true,
      view: "chat",
      pendingSend: text,
      ...(chatOpen ? {} : { agentThreadId: null, messages: [], suggestions: [], resumeTour: null }),
    });
  },
  clearPendingSend: () => set({ pendingSend: null }),

  openThread: (detail) =>
    set({
      view: "chat",
      agentThreadId: detail.id,
      // Carry the persisted section (border color) + tour-step structure (indent, click target),
      // so reloaded threads render colored and stored tours stay interactive.
      messages: detail.messages.map((m) => ({
        id: newId(),
        role: m.role,
        content: m.content,
        section: m.section,
        indent: m.indent ?? undefined,
        anchor: m.anchor ?? undefined,
        route: m.route ?? undefined,
      })),
      suggestions: detail.suggestions ?? [],
      // The loaded session decides: unfinished tour → cursor; anything else clears it.
      resumeTour: detail.resumeTour ?? null,
    }),

  startTurn: (userText, section) => {
    const assistantId = newId();
    set((s) => ({
      pending: true,
      suggestions: [],
      messages: [
        ...s.messages,
        { id: newId(), role: "user", content: userText },
        // `section` drives the live border color of the incoming answer.
        { id: assistantId, role: "assistant", content: "", section },
      ],
    }));
    return assistantId;
  },

  startInfoStream: () => {
    const infoId = newId();
    set((s) => ({
      pending: true,
      messages: [...s.messages, { id: infoId, role: "info", content: "" }],
    }));
    return infoId;
  },

  appendDelta: (assistantId, delta) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === assistantId ? { ...m, content: m.content + delta } : m,
      ),
    })),

  endTurn: () => set({ pending: false }),

  failTurn: (assistantId, error) =>
    set((s) => ({
      pending: false,
      messages: s.messages.map((m) => (m.id === assistantId ? { ...m, error } : m)),
    })),
}));
